import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { createEvent } from '../api'
import { addVisited, getVisited } from '../lib/identity'
import { formatDate, parseKey } from '../lib/date'
import Calendar from '../components/Calendar'
import { Wordmark } from '../components/Logo'

function StepLabel({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="font-display flex h-6 w-6 items-center justify-center rounded-lg bg-brand text-xs font-bold text-white">
        {n}
      </span>
      <span className="font-semibold text-ink">{children}</span>
    </div>
  )
}

const inputCls =
  'w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-ink outline-none transition focus:border-brand focus:ring-4 focus:ring-brand/15 placeholder:text-muted/60'

export default function CreateEvent() {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [addTime, setAddTime] = useState(true)
  const [time, setTime] = useState('19:00〜')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const visited = getVisited()

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys])

  const toggleDate = (key: string) =>
    setSelectedKeys((ks) => (ks.includes(key) ? ks.filter((k) => k !== key) : [...ks, key]))

  const labelFor = (key: string) =>
    formatDate(parseKey(key)) + (addTime && time.trim() ? ` ${time.trim()}` : '')

  const submit = async () => {
    setError('')
    if (!title.trim()) return setError('イベント名を入れてください')
    if (selectedKeys.length === 0) return setError('カレンダーから候補日を選んでください')
    setSubmitting(true)
    try {
      const candidates = selectedKeys.map((k) => ({ label: labelFor(k), date: k }))
      const { id } = await createEvent({ title: title.trim(), description, candidates })
      addVisited(id, title.trim())
      navigate(`/e/${id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : '作成に失敗しました')
      setSubmitting(false)
    }
  }

  return (
    <div className="animate-in mx-auto max-w-3xl px-4 pb-16 pt-5 sm:pt-7">
      {/* ヘッダー */}
      <header className="mb-7 flex items-center justify-between">
        <Wordmark />
        <nav className="flex items-center gap-1.5 text-sm">
          <a
            href="/about"
            className="rounded-lg px-2.5 py-1.5 font-medium text-muted transition hover:bg-brand-soft hover:text-brand-ink"
          >
            使い方
          </a>
          <a
            href="/me"
            className="rounded-lg px-2.5 py-1.5 font-medium text-muted transition hover:bg-brand-soft hover:text-brand-ink"
          >
            マイ予定
          </a>
        </nav>
      </header>

      {/* ヒーロー */}
      <div className="mb-7">
        <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
          みんなの予定、
          <br className="sm:hidden" />
          すっと重ねる。
        </h1>
        <p className="mt-2.5 max-w-md text-[15px] leading-relaxed text-muted">
          ログイン不要のかんたん日程調整。自分の回答も、マイ予定でまとめてひと目で。
        </p>
      </div>

      {/* 入力フォーム */}
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-2xl border border-line bg-surface p-5 shadow-[0_1px_2px_rgba(28,31,51,0.04),0_12px_32px_-20px_rgba(77,85,217,0.25)]">
          <StepLabel n={1}>イベント名</StepLabel>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例）チーム飲み会"
            className={inputCls}
          />
          <label className="mb-1.5 mt-4 block text-sm font-medium text-muted">メモ（任意）</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="例）出欠の〆切は◯日まで"
            className={inputCls + ' resize-none'}
          />
        </section>

        <section className="rounded-2xl border border-line bg-surface p-5 shadow-[0_1px_2px_rgba(28,31,51,0.04),0_12px_32px_-20px_rgba(77,85,217,0.25)]">
          <StepLabel n={2}>日程候補</StepLabel>
          <p className="-mt-1 mb-3 text-xs text-muted">日付をタップして候補に追加できます。</p>

          <label className="mb-2 flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={addTime}
              onChange={(e) => setAddTime(e.target.checked)}
              className="h-4 w-4 rounded accent-brand"
            />
            日付の後に時刻を追加
          </label>
          {addTime && (
            <input
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="19:00〜"
              className={inputCls + ' mb-3'}
            />
          )}

          <Calendar selected={selectedSet} onToggle={toggleDate} />

          {selectedKeys.length > 0 && (
            <div className="mt-3">
              <p className="mb-1.5 text-xs font-medium text-muted">
                選んだ候補 <span className="text-brand">{selectedKeys.length}</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selectedKeys.map((k) => (
                  <button
                    key={k}
                    onClick={() => toggleDate(k)}
                    className="group inline-flex items-center gap-1 rounded-full border border-brand-soft bg-brand-soft px-2.5 py-1 text-xs font-medium text-brand-ink transition hover:border-brand/30"
                  >
                    {labelFor(k)}
                    <span className="text-brand/50 group-hover:text-conflict">✕</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {error && (
        <p className="mt-4 rounded-xl bg-conflict-soft px-4 py-2.5 text-sm font-medium text-conflict">
          {error}
        </p>
      )}

      <button
        onClick={submit}
        disabled={submitting}
        className="mt-5 w-full rounded-2xl bg-brand py-4 font-display text-lg font-bold text-white shadow-[0_8px_24px_-8px_rgba(77,85,217,0.55)] transition hover:bg-brand-ink active:scale-[0.99] disabled:opacity-50"
      >
        {submitting ? '作成中…' : 'イベントをつくる'}
      </button>

      <p className="mt-3 text-center text-sm text-muted">
        調整さんを使っていた方は{' '}
        <a href="/import" className="font-medium text-brand hover:underline">
          こちらから取り込み
        </a>
      </p>

      {visited.length > 0 && (
        <div className="mt-10">
          <h2 className="mb-2.5 text-sm font-semibold text-muted">最近ひらいたイベント</h2>
          <div className="flex flex-col gap-1.5">
            {visited.slice(0, 8).map((v) => (
              <a
                key={v.id}
                href={`/e/${v.id}`}
                className="flex items-center gap-2 rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm font-medium text-ink transition hover:-translate-y-0.5 hover:border-brand/30 hover:bg-brand-soft/40 hover:shadow-sm"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-teal" />
                <span className="truncate">{v.title}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
